import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { signup, login } from '../utils/api';
import { ArrowLeft, Mail, Lock, User } from 'lucide-react';

export default function Auth({ setUser }) {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = isSignup
        ? await signup(formData)
        : await login({ email: formData.email, password: formData.password });

      const userData = response.data.user;
      const token = response.data.session_token;

      setUser(userData);
      localStorage.setItem('session_token', token);
      // Clear guest mode flag when user logs in
      localStorage.removeItem('guest_mode');

      toast.success(isSignup ? 'Account created!' : 'Welcome back!');
      navigate('/level-select');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFD700] via-[#4169E1] to-[#9370DB] p-6" data-testid="auth-page">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-lg shadow-2xl p-8 rounded-3xl">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <h2 className="text-4xl font-bold text-[#4169E1] mb-2 text-center">
          {isSignup ? 'Create Account' : 'Welcome Back'}
        </h2>
        <p className="text-gray-600 text-center mb-8">
          {isSignup ? 'Sign up to start your journey' : 'Sign in to continue'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
          {isSignup && (
            <div>
              <Label htmlFor="name" className="text-gray-700 font-semibold">Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-11 h-12 rounded-xl border-2"
                  placeholder="Enter your name"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-gray-700 font-semibold">Email</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-11 h-12 rounded-xl border-2"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-700 font-semibold">Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                autoComplete={!isSignup ? "current-password" : "new-password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-11 h-12 rounded-xl border-2"
                placeholder="Enter your password"
                required
                minLength={6}
              />
            </div>
          </div>

          <Button
            data-testid="submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 text-lg font-bold bg-[#4169E1] hover:bg-[#3558c7] rounded-xl"
          >
            {loading ? 'Please wait...' : (isSignup ? 'Create Account' : 'Sign In')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="toggle-mode-btn"
            onClick={() => setIsSignup(!isSignup)}
            className="text-[#4169E1] font-semibold hover:underline"
          >
            {isSignup ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>

      </Card>
    </div>
  );
}
